import { createContext, useContext } from "react";
import { useSession as useBetterAuthSession } from "@/lib/auth-client";

// The user shape Better Auth returns includes the additionalField `role`
// from auth.ts (api/src/auth.ts) plus the standard fields. `name` maps
// to the legacy `full_name` column transparently via the Drizzle alias.
export type AuthUser = {
  id: string;
  email: string;
  name: string | null;
  emailVerified: boolean;
  image: string | null;
  role: string;
  createdAt: string | Date;
  updatedAt: string | Date;
};

// Legacy alias kept for callers reading profile.full_name during the
// staged migration. New code should read profile.name directly.
export type AuthProfile = AuthUser & { full_name: string | null };

type AuthContextType = {
  user: AuthUser | null;
  profile: AuthProfile | null;
  loading: boolean;
  error: Error | null;
  isAuthenticated: boolean;
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  loading: true,
  error: null,
  isAuthenticated: false,
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { data, isPending, error } = useBetterAuthSession();

  const user = (data?.user ?? null) as AuthUser | null;
  const profile: AuthProfile | null = user
    ? { ...user, full_name: user.name }
    : null;

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        loading: isPending,
        error: error ? new Error(error.message ?? String(error)) : null,
        isAuthenticated: Boolean(user),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
